import React from 'react';

// Auto-import every .mdx file from src/docs using Vite's import.meta.glob
const modules = import.meta.glob<MDXModule>('../../../../docs/**/*.{md,mdx}', {
  eager: true,
});

// Build an index with component + frontmatter
export type DocEntry = {
  slug: string; // e.g. "test" or "test/subfolder"
  title: string; // human title
  order: number; // sort key
  Component: React.ComponentType<any>; // MDX component
  parentSlug?: string | null; // e.g. "test" for children
  isIndex?: boolean; // true for ".../index.mdx"
};

type MDXModule = {
  default: React.ComponentType<any>;
  frontmatter?: Record<string, any>;
};

const keys = Object.keys(modules);

// Normalize helpers
const LEADING_SEGMENTS_TO_STRIP = ['docs', 'documentation'];

function stripLeadingSegment(path: string) {
  const rx = new RegExp(`^(?:${LEADING_SEGMENTS_TO_STRIP.join('|')})\\/`, 'i');
  return path.replace(rx, '');
}

function normalizeSlug(raw: string) {
  // lower-case, trim slashes, strip leading "docs/" or "documentation/"
  const s = String(raw)
    .toLowerCase()
    .replace(/^\/+|\/+$/g, '');
  return stripLeadingSegment(s);
}

function normalizePathFromKey(key: string) {
  // key looks like "../../../../docs/test/index.mdx"
  // Extract everything after "docs/"
  const match = key.match(/docs\/(.+)$/);
  const relative = match ? match[1] : key.replace(/^.*\//, '');
  const noExt = relative.replace(/\.mdx?$/i, '');
  return normalizeSlug(noExt);
}

function partsOf(pathNoExt: string) {
  return pathNoExt.split('/').filter(Boolean);
}

function inferFromPath(pathNoExt: string) {
  const parts = partsOf(pathNoExt);
  const isIndex = parts[parts.length - 1] === 'index';
  const fileName = parts[parts.length - 1];
  const folderName = parts.length > 1 ? parts[parts.length - 2] : fileName;

  const slug = isIndex
    ? parts.slice(0, -1).join('/') || '' // folder path ('' means root)
    : parts.join('/');

  const parentSlug = isIndex ? null : parts.slice(0, -1).join('/') || null;
  const defaultTitle = isIndex ? folderName || 'docs' : fileName;

  return { slug, parentSlug, isIndex, defaultTitle };
}

const raw = keys
  .map((key: string) => {
    const mod = modules[key];
    const Component = mod?.default;
    if (!Component) return null; // skip broken MDX

    const fm = mod.frontmatter ?? {};
    const pathNoExt = normalizePathFromKey(key);
    const inferred = inferFromPath(pathNoExt);

    // --- Slug resolution (preserve folder path when frontmatter provides a leaf-only slug) ---
    let slug: string;
    if (fm.slug != null) {
      const rawSlug = normalizeSlug(String(fm.slug));
      if (rawSlug === 'index') {
        slug = inferred.slug; // normalize accidental "index" to folder slug
      } else if (rawSlug.includes('/')) {
        slug = rawSlug; // full override path provided
      } else {
        // leaf-only override:
        // - for regular files → prefix with inferred.parentSlug
        // - for index files   → prefix with the *parent of the folder itself*
        if (inferred.isIndex) {
          const folderPath = inferred.slug; // e.g. "test/subfolder"
          const parentOfFolder = folderPath.includes('/') ? folderPath.slice(0, folderPath.lastIndexOf('/')) : '';
          slug = parentOfFolder ? `${parentOfFolder}/${rawSlug}` : rawSlug;
        } else {
          slug = inferred.parentSlug ? `${inferred.parentSlug}/${rawSlug}` : rawSlug;
        }
      }
    } else {
      slug = inferred.slug; // no override → use inferred
    }

    const title = (fm.title ? String(fm.title) : inferred.defaultTitle).replace(/-/g, ' ');
    const order = Number(fm.order ?? 9999);

    // --- Parent is derived from the FINAL slug (works for nested index pages too) ---
    const parentSlug = slug && slug.includes('/') ? slug.slice(0, slug.lastIndexOf('/')) : null;

    const entry: DocEntry = {
      slug,
      title,
      order,
      Component,
      parentSlug,
      isIndex: inferred.isIndex,
    };

    return entry;
  })
  .filter(Boolean) as DocEntry[];

// Dedupe by slug (last one wins)
const deduped = Array.from(new Map(raw.map((d) => [d.slug, d])).values());

// Sort helper
function sortDocs(a: DocEntry, b: DocEntry) {
  return a.order - b.order || a.title.localeCompare(b.title);
}

// Export 1: flat list for routing/search, sorted
export const docsIndexFlat: DocEntry[] = deduped.slice().sort(sortDocs);

// ----- Build a full recursive tree (supports infinite depth) -----
export type DocNode = DocEntry & { children?: DocNode[] };

const byParent = new Map<string | null, DocEntry[]>();
for (const d of docsIndexFlat) {
  const p = d.parentSlug ?? null;
  let list = byParent.get(p);
  if (!list) {
    list = [];
    byParent.set(p, list);
  }
  list.push(d);
}

function buildTree(parent: string | null): DocNode[] {
  const children = (byParent.get(parent) || []).sort(sortDocs);
  return children.map((e) => ({
    ...e,
    children: buildTree(e.slug),
  }));
}

// Export 2: hierarchical tree for the sidebar (root-level nodes are those with parentSlug === null)
export const docsTree: DocNode[] = buildTree(null);

// Back-compat alias (if some files still import { docsIndex })
export const docsIndex = docsIndexFlat;
